import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";
import {
  Phone,
  MessageSquare,
  ShieldCheck,
  Clock,
  Settings,
  Thermometer,
  Box,
  Zap,
  CheckCircle2,
  Tag,
  Award,
  Users,
  MapPin,
  Plus,
  Check,
  X,
} from "lucide-react";

/*
  Replace these image URLs manually later with your Sports Tent & Pavilion images.
  Keep the same constants and arrays so the layout continues to work.
*/

const BANNER_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779692674/Artboard_16_2x_nnackh.webp";

const MOBILE_HERO_TOP_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338727/Artboard_21_xdirvb.webp";

const MOBILE_APPLICATIONS_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338726/Artboard_23_vrdfss.webp";

const MOBILE_BOTTOM_GALLERY_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338727/Artboard_22_cjtzt8.webp";

const GALLERY_TOP = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_3_vdltek.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_4_x7uqh4.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_5_fchtib.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338727/Artboard_6_e59vxk.webp",
];

const GALLERY_APPLICATIONS = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_17_vn9roe.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_18_lnudmf.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_19_msldmj.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338727/Artboard_19_copy_zk1uug.webp",
];

const GALLERY_BOTTOM = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_11_xdt6ow.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_9_ysk5el.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338727/Artboard_8_kx9xih.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_7_hoycki.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_12_thv2gq.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338729/Artboard_13_qabult.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_14_wnhnqf.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779338728/Artboard_15_h2kk1c.webp",
];

const partners = [
  { name: "Emirates Gold Pavilion", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Dubai World Cup", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "World Teakwabdo Championship", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Football Central", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "FIE Equestrian", style: "text-2xl md:text-4xl font-semibold text-white/50" },
];

const specs = [
  { label: "Structure", value: "High-Strength Aluminium Profile" },
  { label: "Clear Span Width", value: "5m to 60m (Customizable)" },
  { label: "Eave Height", value: "2.5m to 12.7m (Customizable)" },
  { label: "Roof Cover", value: "850 GSM White Soft PVC" },
  { label: "Side Cover", value: "Open Sides | PVC Side Walls | Glass | ABS Panel" },
  { label: "Wind Resistance", value: "Up to 145 KPH (ASCEC-16)" },
  { label: "Setup Options", value: "Lighting, Flooring, AC, Branding, Partitions & Seating Support" },
];

const chooseUsFeatures = [
  { icon: ShieldCheck, title: "Event-Ready Strength", desc: "Engineered aluminium structures with durable PVC roofing for sports and outdoor events." },
  { icon: Settings, title: "Custom Pavilion Sizes", desc: "Flexible dimensions for spectator zones, team areas, VIP lounges, and activity spaces." },
  { icon: Clock, title: "Fast Setup", desc: "Quick installation and dismantling for tournaments, seasonal events, and temporary venues." },
  { icon: Zap, title: "Weather Protection", desc: "Reliable shade and shelter against sun, wind, rain, and harsh outdoor conditions." },
  { icon: Thermometer, title: "Cooling Options", desc: "Air conditioning, ventilation, insulation, and misting options can be added." },
  { icon: Box, title: "Rental or Sale", desc: "Cost-effective pavilion tent solutions for short-term events or long-term sports facilities." },
];

const applications = [
  {
    title: "Sports Events & Tournaments",
    desc: "Temporary sports tents and pavilion setups for football, cricket, tennis, athletics, equestrian, and community sports events.",
  },
  {
    title: "Spectator Seating Areas",
    desc: "Covered pavilion tents for audience shade, viewing areas, fan zones, ticketing areas, and waiting lounges.",
  },
  {
    title: "Team & Player Facilities",
    desc: "Practical tent setups for player changing areas, warm-up zones, medical support, equipment storage, and staff rooms.",
  },
  {
    title: "VIP & Hospitality Pavilions",
    desc: "Premium pavilion structures for VIP lounges, sponsor areas, hospitality suites, and branded event experiences.",
  },
  {
    title: "School & Club Sports Days",
    desc: "Safe and comfortable sports day tents for schools, clubs, academies, and community recreation programs.",
  },
  {
    title: "Outdoor Fitness & Recreation",
    desc: "Shade structures and pavilion tents for gyms, wellness activations, outdoor classes, and recreational venues.",
  },
];

const TrustBar = () => {
  const items = [
    { icon: Award, title: "30+", sub: "Years Experience" },
    { icon: Users, title: "8000+", sub: "Projects Delivered" },
    { icon: Tag, title: "Factory Direct", sub: "Factory Pricing" },
    { icon: ShieldCheck, title: "Civil Defense", sub: "Compliant Tents" },
    { icon: MapPin, title: "UAE-Wide", sub: "Fast Installation" },
  ];

  return (
    <div className="grid grid-cols-2 md:flex md:flex-nowrap items-start md:items-center gap-4 md:gap-6 mt-6 border-t border-white/10 pt-6">
      {items.map((item, i) => (
        <div
          key={i}
          className={`flex items-start md:items-center gap-3 ${
            i === 4 ? "col-span-2 md:col-span-1" : ""
          } ${i === 3 ? "md:ml-4" : ""}`}
        >
          <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
            <item.icon className="w-5 h-5 text-[#bd6aff]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-white font-bold text-sm leading-none whitespace-nowrap">{item.title}</span>
            <span className="text-zinc-200 text-[10px] uppercase tracking-wider font-bold mt-1.5">{item.sub}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SportsTentPavilion = () => {
  const navigate = useNavigate();
  const quoteRef = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    document.title = "Sports Tent & Pavilion Setup UAE | BA Tents";

    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "BA Tents provides sports tents, pavilion setups, spectator shade structures, VIP sports pavilions, and temporary event tents for rental and sale across UAE. Fast installation, durable PVC structures, and customizable sports shelter solutions."
      );
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    window.scrollTo(0, 0);

    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", {
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code,
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
      }),
    }).catch(() => {});
  };

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white">
        <section className="relative min-h-[60vh] md:min-h-[90vh] w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12 overflow-hidden flex flex-col bg-black">
          <img
            src={BANNER_IMAGE}
            className="hidden md:block absolute inset-0 w-full h-full object-cover z-0 opacity-60 scale-105"
            alt="Sports tent and pavilion setup UAE"
            style={{ objectPosition: "center top" }}
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/40 via-transparent to-black z-[1]" />

          <div className="flex-1 relative z-10 flex flex-col items-center justify-center px-4 md:px-0">
            <div className="w-full max-w-[1440px] grid grid-cols-1 md:grid-cols-2 gap-8 items-center md:px-12">
              <div className="w-full mb-6 p-4 md:p-0 md:-ml-12 bg-transparent">
                <div className="mt-24 inline-block px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    Sports <span className="text-purple-400">Tent & Pavilion Setup in UAE</span>
                  </span>
                </div>

                <h1 className="text-[32px] md:text-[58px] font-black text-white leading-[1.1] mb-5 tracking-tight uppercase">
                  Sports Tent <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    & Pavilion Setup
                  </span>{" "}
                  <br />
                  <span className="text-[22px] md:text-[38px] text-white/90">in UAE & Dubai</span>
                </h1>

                <div className="mb-8 max-w-2xl">
                  <p className="hidden md:block text-zinc-300 text-sm md:text-base font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> provides sports tents, pavilion setups, spectator shade structures, and temporary event shelters for rental and sale across the UAE.
                  </p>

                  <p className="block md:hidden text-zinc-300 text-sm font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> provides sports tents, pavilion setups, spectator shade structures, and temporary event shelters across the UAE.
                  </p>
                </div>

                <div className="flex flex-row items-center gap-1 md:gap-2 -mt-2 mb-3 md:mb-6 w-full max-w-lg">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-purple-500/10 md:bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] border border-purple-400/40 md:border-0 rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-black/20" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white/10 md:bg-white border border-white/20 md:border-0 rounded-xl text-white md:text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-white md:fill-black/20 text-white md:text-black" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500/10 md:bg-emerald-500 border border-emerald-500/40 md:border-0 rounded-xl text-emerald-400 md:text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="max-w-lg">
                  <TrustBar />
                </div>

                {!isDesktop && (
                  <div className="relative w-full mt-8 -mb-9 overflow-hidden rounded-2xl bg-zinc-900 border border-white/10 shadow-2xl">
                    <img
                      src={MOBILE_HERO_TOP_IMAGE}
                      className="w-full h-auto object-cover"
                      alt="Sports pavilion setup mobile gallery"
                      width={800}
                      height={1000}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="hidden md:block" />
            </div>
          </div>
        </section>

        <section className="hidden md:block mt-12 w-full max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-1">
            {GALLERY_TOP.map((img, idx) => (
              <div key={`top-${idx}`} className="aspect-[4/3] overflow-hidden rounded-lg border border-white/10 bg-zinc-900">
                <img
                  src={img}
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={450}
                  className="w-full h-full object-cover transition-all duration-500"
                  alt={`Sports Pavilion Grid ${idx + 1}`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 bg-black relative overflow-hidden border-t border-white/5">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
          <div className="flex flex-col gap-4">
            <p className="text-center text-[10px] font-black text-zinc-300 uppercase tracking-[0.3em] mb-4">
              Trusted for Sports, Events & Outdoor Venues
            </p>

            <div className="flex overflow-hidden group">
              <div className="flex flex-nowrap gap-16 items-center whitespace-nowrap px-8 animate-[marquee_30s_linear_infinite]">
                {[...partners, ...partners].map((partner, idx) => (
                  <span key={idx} className={`${partner.style} tracking-tighter opacity-60 hover:opacity-100 transition-opacity uppercase`}>
                    {partner.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pt-4 pb-8 md:py-12 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-4 items-stretch">
            <div className="lg:col-span-2 relative p-6 md:p-8 rounded-[2rem] bg-[#121212] border border-zinc-800/60 shadow-2xl flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-black mb-6 md:mb-8 uppercase tracking-tighter text-white">
                  Technical <span className="text-purple-500">Specifications</span>
                </h2>

                <table className="w-full text-left text-xs border-collapse">
                  <tbody>
                    {specs.map((s, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                        <td className="py-4 font-bold text-zinc-400 uppercase text-[9px] tracking-widest w-1/3">
                          {s.label}
                        </td>
                        <td className="py-4 text-zinc-200 font-medium text-sm">
                          {s.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div 
              ref={quoteRef} 
              className="relative p-6 md:p-8 rounded-3xl bg-[#0F0F0F] border-2 border-purple-900/60 shadow-[0_0_30px_rgba(139,49,255,0.15)] flex flex-col gap-4 overflow-hidden"
            >
              <div className="flex flex-col gap-1 border-b-2 border-zinc-900 pb-5">
                <h2 className="text-xl font-bold text-white tracking-tighter uppercase">Quick Quote</h2>
                <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
                  Immediate 15-Minute Response
                </span>
              </div>

              <form className="space-y-4 pt-2" onSubmit={handleMiniSubmit}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
                    {geo.code}
                  </div>
                  <input
                    type="tel"
                    required
                    placeholder="Mobile Number"
                    className="w-full bg-[#121212] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <textarea
                  placeholder="Tell us about your sports tent or pavilion requirements..."
                  className="w-full h-28 bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                />

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.2)] active:scale-95 transition-all hover:brightness-110"
                >
                  Send Request
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="pt-8 pb-16 md:py-20 max-w-7xl mx-auto px-6 border-t border-white/5">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">
              Ideal for Sports <span className="text-[#bd6aff]">Events & Venues</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {applications.map((app, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="mt-1">
                    <CheckCircle2 className="text-[#bd6aff] w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-1 group-hover:text-[#bd6aff] transition-colors">
                      {app.title}
                    </h3>
                    <p className="text-zinc-300 text-[11px] leading-relaxed max-w-md">{app.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:grid grid-cols-2 gap-3">
              {GALLERY_APPLICATIONS.map((img, idx) => (
                <div key={`app-img-${idx}`} className="relative overflow-hidden rounded-xl border border-white/5 bg-zinc-900 shadow-2xl aspect-[16/10]">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out hover:scale-105"
                    alt="Sports tent application"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80" />
                </div>
              ))}
            </div>

            <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-2xl">
              <img
                src={MOBILE_APPLICATIONS_IMAGE}
                className="w-full h-auto object-cover"
                alt="Sports tent applications mobile gallery"
                width={800}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="text-center mb-12 mt-[80px]">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase">
              Why Choose Our Sports Tents?
            </h2>
            <p className="text-zinc-300 text-sm max-w-2xl mx-auto uppercase tracking-widest">
              Professional pavilion setups for outdoor sports and events
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {chooseUsFeatures.map((f, i) => (
              <div key={i} className="p-6 bg-zinc-900/50 border border-white/5 rounded-2xl flex flex-col items-center text-center hover:bg-zinc-900 transition-colors group">
                <f.icon className="w-8 h-8 text-[#bd6aff] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-[11px] font-bold mb-2 tracking-tight uppercase text-white">{f.title}</h3>
                <p className="text-zinc-300 text-[10px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-2 md:px-6 space-y-2">
          <div className="hidden md:block space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(0, 4).map((img, idx) => (
                <div key={`bottom-1-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Sports pavilion project"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(4, 8).map((img, idx) => (
                <div key={`bottom-2-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Sports tent installation"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
            <img
              src={MOBILE_BOTTOM_GALLERY_IMAGE}
              className="w-full h-auto object-cover"
              alt="Sports tent projects mobile gallery"
              width={800}
              height={1000}
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Tag className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Sports Tent <span className="text-purple-500">Sale</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Direct factory pricing for permanent sports pavilions, shaded activity zones, club facilities, and long-term outdoor venue structures.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "5m - 60m Clear Span Widths",
                      "Custom Length for Venue Layouts",
                      "VIP, Spectator & Team Area Options",
                      "Branding and Lighting Integration",
                      "Fire-Retardant & UV Protection",
                      "Engineered for UAE Weather",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Tag className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Clock className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Sports Tent <span className="text-purple-500">Rental</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Fast deployment for tournaments, sports days, temporary spectator areas, outdoor fitness activations, and seasonal sports events.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "Flexible Rental Inventory",
                      "Rapid Installation & Removal",
                      "Short-Term and Monthly Terms",
                      "Open, Closed, or Semi-Closed Layouts",
                      "Optional AC, Flooring & Lighting",
                      "Expandable Event Capacity",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Clock className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-b from-[#bd6aff]/20 to-transparent border border-[#bd6aff]/30 lg:col-span-1 md:col-span-2">
              <div className="bg-zinc-950/80 p-8 h-full flex flex-col">
                <h2 className="text-lg font-black uppercase tracking-tight text-white mb-8 text-center">
                  Rental vs Sale Comparison
                </h2>

                <div className="space-y-5">
                  <div className="grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 pb-3 border-b border-white/5">
                    <div>Feature</div>
                    <div className="text-center">Rental</div>
                    <div className="text-center">Sale</div>
                  </div>

                  {[
                    { f: "Event Use", r: true, s: true },
                    { f: "Long-Term Venue ROI", r: false, s: true },
                    { f: "Maintenance", r: "Incl.", s: "Opt." },
                    { f: "Lead Time", r: "Fast", s: "Custom" },
                    { f: "Asset Ownership", r: false, s: true },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-3 items-center py-4 border-t border-white/5">
                      <div className="text-[10px] font-bold text-zinc-300 uppercase leading-none">{row.f}</div>
                      <div className="flex justify-center">
                        {row.r === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.r === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.r}</span>}
                      </div>
                      <div className="flex justify-center">
                        {row.s === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.s === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.s}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-32 max-w-5xl mx-auto px-6 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none" />

          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase text-white">
              Sports Tent <span className="text-[#bd6aff]">FAQ</span>
            </h2>
            <p className="text-zinc-300 text-[10px] uppercase tracking-[0.3em]">
              Expert Pavilion Setup Insights
            </p>
          </div>

          <div className="space-y-4 relative z-10">
            {[
              {
                q: "Do you provide sports tents and pavilion setups on rental basis in UAE?",
                a: "Yes. BA Tents provides sports tent rental, pavilion setup rental, spectator shade tents, VIP pavilions, and temporary event shelters for sports events across Dubai, Abu Dhabi, Sharjah, and all Emirates.",
              },
              {
                q: "Do you offer sports tents and pavilions for sale?",
                a: "Yes. We manufacture and supply custom sports tents and pavilion structures for long-term use at clubs, schools, academies, recreational facilities, and outdoor venues.",
              },
              {
                q: "Can the pavilion setup be customized for our event layout?",
                a: "Yes. We can customize span width, length, side walls, entrances, flooring, lighting, air conditioning, partitions, branding, and interior layout based on your event requirements.",
              },
              {
                q: "Are these tents suitable for spectator seating and VIP areas?",
                a: "Yes. Our clear-span tent systems are suitable for covered spectator zones, hospitality areas, VIP lounges, registration counters, and fan engagement spaces.",
              },
              {
                q: "How quickly can you install a sports tent or pavilion?",
                a: "Installation time depends on size, site access, and accessories, but our modular systems are designed for fast setup and removal for temporary sports events.",
              },
              {
                q: "Are your sports tents suitable for UAE outdoor weather?",
                a: "Yes. Our sports tents use heavy-duty aluminium structures and fire-retardant PVC fabric designed for outdoor conditions, UV exposure, wind, and high temperatures.",
              },
            ].map((item, idx) => (
              <div key={idx} className="group p-[1px] rounded-2xl bg-white/10 border border-white/5 hover:border-[#bd6aff]/40 transition-all duration-300 shadow-xl">
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="text-sm font-bold text-zinc-200 uppercase tracking-tight group-hover:text-white transition-colors">
                      {item.q}
                    </span>
                    <Plus className="w-4 h-4 text-[#bd6aff] group-open:rotate-45 transition-transform duration-300" />
                  </summary>
                  <div className="px-6 pb-6 text-zinc-400 text-xs leading-relaxed border-t border-white/5 pt-4">
                    {item.a}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </section>
      </div>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default SportsTentPavilion;
